// src/components/backoffice/coupons/CouponsPage.tsx
// לשונית ניהול קופוני גישה חופשית

import React, { useState, useEffect, useCallback } from 'react';
import { couponsService, Coupon } from '../../../services/backoffice/couponsService';

const formatDateTime = (s?: string): string => {
  if (!s) return '-';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const CouponsPage: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // טופס יצירה
  const [showForm, setShowForm] = useState(false);
  const [formCode, setFormCode] = useState('');
  const [formCampaign, setFormCampaign] = useState('');
  const [formDuration, setFormDuration] = useState('72');

  const loadCoupons = useCallback(async () => {
    try {
      setLoading(true);
      const data = await couponsService.getCoupons();
      setCoupons(data);
    } catch (err: any) {
      setError(`שגיאה בטעינת קופונים: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCoupons(); }, [loadCoupons]);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(''), 4000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  const resetForm = () => {
    setFormCode('');
    setFormCampaign('');
    setFormDuration('72');
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!formCode.trim()) { setError('קוד הקופון הוא שדה חובה'); return; }
    if (!formCampaign.trim()) { setError('שם המבצע הוא שדה חובה'); return; }
    const dur = parseInt(formDuration, 10);
    if (!Number.isInteger(dur) || dur <= 0) { setError('משך בשעות חייב להיות מספר חיובי'); return; }

    try {
      setSaving(true);
      setError('');
      await couponsService.createCoupon({
        code: formCode.trim(),
        campaign_name: formCampaign.trim(),
        duration_hours: dur,
      });
      setSuccessMessage('הקופון נוצר והופעל בהצלחה');
      resetForm();
      await loadCoupons();
    } catch (err: any) {
      setError(`שגיאה ביצירת הקופון: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (coupon: Coupon) => {
    try {
      setError('');
      await couponsService.toggleCoupon(coupon.id, !(coupon.is_active === 1));
      setSuccessMessage(coupon.is_active === 1 ? 'הקופון כובה' : 'הקופון הופעל');
      await loadCoupons();
    } catch (err: any) {
      setError(`שגיאה בעדכון הקופון: ${err.message}`);
    }
  };

  // תווית סטטוס: פעיל כעת / כובה ידנית / פג תוקף
  const statusBadge = (c: Coupon) => {
    if (c.is_active !== 1) {
      return <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">כבוי</span>;
    }
    if (c.currently_active === 1 || c.currently_active === undefined) {
      const stillOpen = new Date(c.window_end).getTime() > Date.now();
      if (stillOpen) {
        return <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">פעיל</span>;
      }
    }
    return <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">פג תוקף</span>;
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ניהול קופונים</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          + קופון חדש
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      {/* טופס יצירה */}
      {showForm && (
        <div className="bg-white rounded-xl shadow p-5 mb-6 border-r-4 border-blue-500">
          <h2 className="text-lg font-semibold text-gray-800 mb-1">קופון חדש</h2>
          <p className="text-xs text-gray-500 mb-4">
            הקופון מופעל מיידית. החלון נמשך מרגע היצירה למשך מספר השעות שתבחר — כל מי שיזין את הקוד יקבל גישה עד תום החלון המשותף.
          </p>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label htmlFor="cp-code" className="block text-sm font-medium text-gray-700 mb-1">
                קוד קופון <span className="text-red-500">*</span>
              </label>
              <input
                id="cp-code"
                type="text"
                value={formCode}
                onChange={(e) => setFormCode(e.target.value)}
                aria-required="true"
                placeholder="FORUM72"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="cp-campaign" className="block text-sm font-medium text-gray-700 mb-1">
                שם מבצע <span className="text-red-500">*</span>
              </label>
              <input
                id="cp-campaign"
                type="text"
                value={formCampaign}
                onChange={(e) => setFormCampaign(e.target.value)}
                aria-required="true"
                placeholder="הטבה לחברי הפורום"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="cp-duration" className="block text-sm font-medium text-gray-700 mb-1">
                משך (שעות) <span className="text-red-500">*</span>
              </label>
              <input
                id="cp-duration"
                type="number"
                min="1"
                value={formDuration}
                onChange={(e) => setFormDuration(e.target.value)}
                aria-required="true"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              ביטול
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'יוצר...' : 'צור והפעל'}
            </button>
          </div>
        </div>
      )}

      {/* טבלת קופונים + מעקב המרה */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">טוען קופונים...</div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-12 text-gray-500">אין קופונים עדיין</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-right px-4 py-3 font-medium text-gray-600">קוד</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">מבצע</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">משך</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">תחילה</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">סיום</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600" title="כמה משתמשים השתמשו בקופון">השתמשו</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600" title="חדשים שהמירו למנוי בתשלום">המירו</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">סטטוס</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono font-medium text-gray-800">{c.code}</td>
                  <td className="px-4 py-3 text-gray-700">{c.campaign_name}</td>
                  <td className="px-4 py-3 text-gray-600">{c.duration_hours} ש'</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDateTime(c.window_start)}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDateTime(c.window_end)}</td>
                  <td className="px-4 py-3 text-center text-gray-800 font-medium">{c.total_used ?? 0}</td>
                  <td className="px-4 py-3 text-center text-green-700 font-medium">{c.total_converted ?? 0}</td>
                  <td className="px-4 py-3">{statusBadge(c)}</td>
                  <td className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleToggle(c)}
                      className={`px-3 py-1 rounded border transition-colors ${
                        c.is_active === 1
                          ? 'text-orange-600 border-orange-200 hover:bg-orange-50'
                          : 'text-green-600 border-green-200 hover:bg-green-50'
                      }`}
                    >
                      {c.is_active === 1 ? 'כבה' : 'הפעל'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-400 px-4 py-3 border-t">
            "המירו" = משתמשים חדשים שהגיעו דרך הקופון ורכשו מנוי בתשלום לאחר מכן.
          </p>
        </div>
      )}
    </div>
  );
};

export default CouponsPage;
